import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Collapse,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import { getLocations, getPcps } from "../../../../utils/services/control/masterDataService";

const memberTypeOptions = [
  { label: "MED", value: "MED" },
  { label: "ACA", value: "ACA" },
];

const genderOptions = [
  { label: "Male", value: "M" },
  { label: "Female", value: "F" },
];

const PatientMapFilters = ({
  memberFullName,    setMemberFullName,
  locationId,        setLocationId,
  memberType,        setMemberType,
  pcpName,           setPcpName,
  gender,            setGender,
  ageFrom,           setAgeFrom,
  ageTo,             setAgeTo,
  dateOfBirth,       setDateOfBirth,
  filterAdmitted,    setFilterAdmitted,
  filterDischarged,  setFilterDischarged,
  filterEmergency,   setFilterEmergency,
  filterHighRisk,    setFilterHighRisk,
  onApply,
}) => {
  const [locationOptions, setLocationOptions] = useState([]);
  const [pcpOptions, setPcpOptions] = useState([]);
  const [catalogsLoading, setCatalogsLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCatalogsLoading(true);

    Promise.all([
      getLocations({ status: 1, clinical: true }).catch((e) => {
        console.error("Error loading locations:", e);
        return [];
      }),
      getPcps({ status: 1 }).catch((e) => {
        console.error("Error loading PCPs:", e);
        return [];
      }),
    ])
      .then(([locs, pcps]) => {
        if (cancelled) return;

        const mappedLocs = (Array.isArray(locs) ? locs : [])
          .map((l) => ({
            id: l.locationId,
            name: l.medicalCenter || l.centerKey || `Location ${l.locationId}`,
          }))
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setLocationOptions(mappedLocs);

        const mappedPcps = (Array.isArray(pcps) ? pcps : [])
          .map((p) => ({
            value: p.pcpFullName,
            label: p.title ? `${p.pcpFullName} (${p.title})` : p.pcpFullName,
          }))
          .filter((p) => !!p.value);

        const dedupedPcps = Array.from(
          new Map(mappedPcps.map((p) => [p.value, p])).values()
        ).sort((a, b) => a.label.localeCompare(b.label));
        setPcpOptions(dedupedPcps);
      })
      .finally(() => {
        if (!cancelled) setCatalogsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const dobValue = dateOfBirth ? dayjs(dateOfBirth) : null;

  const handleDobChange = (newValue) => {
    if (!newValue || !newValue.isValid?.()) {
      setDateOfBirth(null);
      return;
    }
    setDateOfBirth(newValue.format("YYYY-MM-DD"));
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {/* Row 1 — Name · DOB · Gender */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Search Patient by Name"
              placeholder="Type a name or CIP_ID"
              value={memberFullName ?? ""}
              onChange={(e) => setMemberFullName(e.target.value || null)}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <DatePicker
              label="Date of Birth"
              value={dobValue}
              onChange={handleDobChange}
              format="YYYY-MM-DD"
              slotProps={{
                textField: { size: "small", fullWidth: true },
                field: { clearable: true, onClear: () => setDateOfBirth(null) },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Gender</InputLabel>
              <Select
                label="Gender"
                value={gender ?? ""}
                onChange={(e) => setGender(e.target.value || null)}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                {genderOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Row 2 — Location · PCP */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Location</InputLabel>
              <Select
                label="Location"
                value={locationId ?? ""}
                onChange={(e) =>
                  setLocationId(e.target.value === "" ? null : Number(e.target.value))
                }
              >
                <MenuItem value=""><em>All</em></MenuItem>
                {locationOptions.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Autocomplete
              size="small"
              fullWidth
              options={pcpOptions}
              loading={catalogsLoading}
              value={pcpOptions.find((o) => o.value === pcpName) || null}
              onChange={(_, newOption) => setPcpName(newOption ? newOption.value : null)}
              getOptionLabel={(o) => (typeof o === "string" ? o : o?.label || "")}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              slotProps={{
                popper: {
                  style: { width: "fit-content", minWidth: 360 },
                  placement: "bottom-start",
                },
                paper: { sx: { minWidth: 360 } },
              }}
              renderInput={(params) => (
                <TextField {...params} label="PCP" placeholder="All" />
              )}
            />
          </Grid>
        </Grid>

        {/* Row 3 — Clinical flags */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!filterHighRisk}
                onChange={(e) => setFilterHighRisk(e.target.checked ? true : null)}
              />
            }
            label="High Risk"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!filterAdmitted}
                onChange={(e) => setFilterAdmitted(e.target.checked ? true : null)}
              />
            }
            label="Admitted"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!filterDischarged}
                onChange={(e) => setFilterDischarged(e.target.checked ? true : null)}
              />
            }
            label="Discharged"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!filterEmergency}
                onChange={(e) => setFilterEmergency(e.target.checked ? true : null)}
              />
            }
            label="Emergency"
          />
        </Box>

        {/* Additional filters toggle + Apply */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Button
            size="small"
            variant="text"
            color="inherit"
            startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowAdvanced((s) => !s)}
            sx={{ textTransform: "none", alignSelf: { xs: "flex-start", sm: "center" } }}
          >
            {showAdvanced ? "Hide additional filters" : "Show additional filters"}
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={onApply}
            sx={{ height: "40px", minWidth: { sm: 180 } }}
          >
            Apply Filters
          </Button>
        </Stack>

        <Collapse in={showAdvanced} timeout={250} unmountOnExit>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mt: 2 }}
          >
            <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
              <InputLabel>Member Type</InputLabel>
              <Select
                label="Member Type"
                value={memberType ?? ""}
                onChange={(e) => setMemberType(e.target.value || null)}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                {memberTypeOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              sx={{ flex: 1 }}
              label="Age From"
              type="number"
              value={ageFrom ?? ""}
              onChange={(e) => setAgeFrom(e.target.value ? +e.target.value : null)}
            />

            <TextField
              size="small"
              sx={{ flex: 1 }}
              label="Age To"
              type="number"
              value={ageTo ?? ""}
              onChange={(e) => setAgeTo(e.target.value ? +e.target.value : null)}
            />
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default PatientMapFilters;
